import { useEffect, useMemo, useRef, useState } from "react";
import { Calendar, DatePicker } from "@material-ui/pickers";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import CompactDateSelector from "./CompactDateSelector";
import ImageCalendarDay from "./ImageCalendarDay";
import { useViewLandContext } from "../ViewLandContext";
import { useViewImageContext } from "./ViewImageContext";
import { getImageQuality } from "./imageTypeUtils";
import { useIsMobile } from "../../../../../../shared/responsive";
import { scrollElementIntoMobilePanel } from "../../../../../../shared/scrollIntoMobilePanel";
import "./DateSelector.style.scss";

const DateSelector = ({ openSignal = 0, onOpenSignalHandled }) => {
  const viewLandContext = useViewLandContext();
  const viewImageContext = useViewImageContext();
  const isMobile = useIsMobile();
  const [openedDatePicker, setOpenedDatePicker] = useState(false);
  const [openedMobileCalendar, setOpenedMobileCalendar] = useState(false);
  const { t } = useTranslation();
  const [cloudinessFilter, setCloudinessFilter] = useState(20);
  const [debouncedCloudinessFilter, setDebouncedCloudinessFilter] = useState(cloudinessFilter);
  const lastLandId = useRef(null);
  const datesRequestId = useRef(0);
  const mobileCalendarRef = useRef(null);
  const lastHandledOpenSignal = useRef(0);
  const { setDates, setCloudyDates, setDatesState, getLandDatesCache, ensureLandDates } = viewImageContext;
  const landId = viewLandContext.land?.land_id;
  const imageSnapshot = viewImageContext.getLandImageSnapshot(landId, debouncedCloudinessFilter);
  const activeImageQuality = getImageQuality(imageSnapshot.imageType);
  const activeDatesKey = `${landId || "no-land"}:${activeImageQuality}:all`;
  const isLoadingDates = imageSnapshot.datesState?.status === "loading";
  const datesUnavailable = imageSnapshot.datesState?.status === "error";
  const selectedDate = imageSnapshot.selectedDate;
  const clearDates = imageSnapshot.clearDates;
  const cloudyDatesList = imageSnapshot.cloudyDatesList;
  const availableDates = useMemo(() => {
    return Array.from(new Set([...(clearDates || []), ...(cloudyDatesList || [])]))
      .sort((left, right) => new Date(right) - new Date(left));
  }, [clearDates, cloudyDatesList]);
  const navigableDates = useMemo(() => {
    return [...(clearDates || [])].sort((left, right) => new Date(right) - new Date(left));
  }, [clearDates]);
  const selectedDateValue = selectedDate ? format(selectedDate, "yyyy-MM-dd") : "";
  const selectedDateTime = selectedDateValue ? new Date(`${selectedDateValue}T00:00:00`).getTime() : null;
  const previousClearDate = useMemo(() => {
    if (selectedDateTime == null) {
      return null;
    }

    return navigableDates.find((dateValue) => new Date(`${dateValue}T00:00:00`).getTime() < selectedDateTime) || null;
  }, [navigableDates, selectedDateTime]);
  const nextClearDate = useMemo(() => {
    if (selectedDateTime == null) {
      return null;
    }

    return [...navigableDates]
      .reverse()
      .find((dateValue) => new Date(`${dateValue}T00:00:00`).getTime() > selectedDateTime) || null;
  }, [navigableDates, selectedDateTime]);
  const hasAvailableDates = availableDates.length > 0;
  const canGoPrev = previousClearDate != null;
  const canGoNext = nextClearDate != null;
  const inlineCalendarDate = useMemo(() => {
    if (selectedDate) {
      return selectedDate;
    }

    if (availableDates.length > 0) {
      return new Date(`${availableDates[0]}T00:00:00`);
    }

    return new Date();
  }, [availableDates, selectedDate]);
  const isCalendarOpen = isMobile ? openedMobileCalendar : openedDatePicker;

  useEffect(() => {
    if (
      openSignal <= lastHandledOpenSignal.current ||
      viewImageContext.loading ||
      isLoadingDates ||
      !hasAvailableDates
    ) {
      return;
    }

    lastHandledOpenSignal.current = openSignal;
    onOpenSignalHandled?.(openSignal);

    if (isMobile) {
      setOpenedMobileCalendar(true);
      return;
    }

    setOpenedDatePicker(true);
  }, [
    hasAvailableDates,
    isLoadingDates,
    isMobile,
    onOpenSignalHandled,
    openSignal,
    viewImageContext.loading,
  ]);

  useEffect(() => {
    if (!isMobile || !openedMobileCalendar) {
      return undefined;
    }

    return scrollElementIntoMobilePanel(mobileCalendarRef.current, { delay: 160 });
  }, [isMobile, openedMobileCalendar, availableDates.length]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedCloudinessFilter(cloudinessFilter);
    }, 800);

    return () => {
      clearTimeout(handler);
    };
  }, [cloudinessFilter]);

  useEffect(() => {
    if (landId == null) {
      return;
    }

    const cached = getLandDatesCache(landId, debouncedCloudinessFilter, activeImageQuality);
    if (cached && (cached.status === "ready" || cached.status === "empty" || cached.status === "error")) {
      setDates(cached.clearDates || []);
      setCloudyDates(cached.cloudyDatesList || []);
      setDatesState(landId, cached, activeImageQuality);
      lastLandId.current = activeDatesKey;
      return;
    }

    const currentRequestId = datesRequestId.current + 1;
    datesRequestId.current = currentRequestId;

    if (lastLandId.current !== activeDatesKey) {
      setDates([]);
      setCloudyDates([]);
      setDatesState(landId, {
        status: "loading",
        availableDates: 0,
        cloudyDates: 0,
        cloudinessFilter: debouncedCloudinessFilter,
      }, activeImageQuality);
      lastLandId.current = activeDatesKey;
    } else {
      setDatesState(landId, {
        status: "loading",
        cloudinessFilter: debouncedCloudinessFilter,
      }, activeImageQuality);
    }

    ensureLandDates(landId, debouncedCloudinessFilter, activeImageQuality)
      .then((datesState) => {
        if (currentRequestId !== datesRequestId.current) {
          return;
        }

        setDates(datesState.clearDates || []);
        setCloudyDates(datesState.cloudyDatesList || []);
      })
      .catch(() => {
        if (currentRequestId !== datesRequestId.current) {
          return;
        }

        setDates([]);
        setCloudyDates([]);
        setDatesState(landId, {
          status: "error",
          availableDates: 0,
          cloudyDates: 0,
          cloudinessFilter: debouncedCloudinessFilter,
        }, activeImageQuality);
      });
  }, [activeDatesKey, activeImageQuality, debouncedCloudinessFilter, ensureLandDates, getLandDatesCache, landId, setCloudyDates, setDates, setDatesState]);

  const changeDateByValue = (dateValue) => {
    if (!dateValue) {
      return;
    }

    viewImageContext.changeDate(viewLandContext.land, new Date(`${dateValue}T00:00:00`));
  };

  const handleNextDate = () => {
    if (!nextClearDate) {
      return;
    }

    changeDateByValue(nextClearDate);
  };

  const handlePrevDate = () => {
    if (!previousClearDate) {
      return;
    }

    changeDateByValue(previousClearDate);
  };

  const getDateOptionLabel = (dateValue) => {
    const date = new Date(`${dateValue}T00:00:00`);
    const statusLabel = clearDates.includes(dateValue)
      ? t("images:date_status.clear")
      : t("images:date_status.cloudy");
    return `${format(date, "dd/MM/yyyy")} · ${statusLabel}`;
  };

  const triggerLabel = isLoadingDates
    ? t("images:dates_loading")
    : selectedDate
      ? getDateOptionLabel(selectedDateValue)
      : t("images:date_selector");

  if (isMobile && isLoadingDates && selectedDate == null && !hasAvailableDates) {
    return null;
  }

  const handleOpenCalendar = () => {
    if (viewImageContext.loading || isLoadingDates || !hasAvailableDates) {
      return;
    }

    if (isMobile) {
      setOpenedMobileCalendar((prev) => !prev);
      return;
    }

    setOpenedDatePicker(true);
  };

  const retryDates = () => {
    if (!landId || isLoadingDates) {
      return;
    }

    ensureLandDates(landId, debouncedCloudinessFilter, activeImageQuality, {force: true})
      .then((datesState) => {
        setDates(datesState.clearDates || []);
        setCloudyDates(datesState.cloudyDatesList || []);
      })
      .catch(() => {});
  };

  const renderCalendarDay = (day, pickerSelectedDate, dayInCurrentMonth, dayComponent) => {
    const formattedDate = format(day, "yyyy-MM-dd");
    let extraClass = "";

    if (pickerSelectedDate != null && pickerSelectedDate.getTime() === day.getTime()) {
      extraClass = "active";
    }

    const isDateWithData = clearDates.includes(formattedDate);
    const isDateWithCloud = cloudyDatesList.includes(formattedDate);
    const cloudState = isDateWithData
      ? "clear"
      : isDateWithCloud
        ? "cloudy"
        : null;
    const tooltipMessage = isDateWithData
      ? `Nubosidad menor a ${cloudinessFilter}%`
      : isDateWithCloud
        ? `Nubosidad mayor a ${cloudinessFilter}%`
        : "Sin datos de nubosidad";

    return (
      <ImageCalendarDay inCurrentMonth={dayInCurrentMonth} cloudState={cloudState}
        active={extraClass === 'active'} title={tooltipMessage}>
        {dayComponent}
      </ImageCalendarDay>
    );
  };

  const shouldDisableCalendarDate = (date) => {
    const formattedDate = format(date, "yyyy-MM-dd");
    return clearDates.indexOf(formattedDate) === -1 && cloudyDatesList.indexOf(formattedDate) === -1;
  };

  return (
    <>
      <CompactDateSelector
        canGoPrev={canGoPrev} canGoNext={canGoNext}
        onPrev={handlePrevDate} onNext={handleNextDate} onOpen={handleOpenCalendar}
        disabled={viewImageContext.loading || isLoadingDates || !hasAvailableDates}
        isCalendarOpen={isCalendarOpen} isLoadingDates={isLoadingDates} triggerLabel={triggerLabel}
        cloudinessFilter={cloudinessFilter} onCloudinessChange={setCloudinessFilter}
      />

      {datesUnavailable && !hasAvailableDates && (
        <div className="alert alert-warning view-land-date-selector__alert" role="alert">
          <span>{t("images:dates_unavailable")}</span>
          <button type="button" className="btn btn-sm btn-warning" onClick={retryDates}>
            {t("images:retry_dates")}
          </button>
        </div>
      )}

      {isMobile && openedMobileCalendar && availableDates.length > 0 && (
        <div ref={mobileCalendarRef} className="view-land-date-selector__inline-calendar">
          <Calendar
            date={inlineCalendarDate}
            onChange={(date) => {
              viewImageContext.changeDate(viewLandContext.land, date);
              setOpenedMobileCalendar(false);
            }}
            renderDay={renderCalendarDay}
            shouldDisableDate={shouldDisableCalendarDate}
            allowKeyboardControl={false}
          />
        </div>
      )}

      {!isMobile && viewImageContext.getActiveLandImage(landId).land && (
        <DatePicker
          open={openedDatePicker}
          value={selectedDate}
          autoOk
          okLabel={null}
          cancelLabel={t("common:actions.cancel")}
          renderDay={renderCalendarDay}
          shouldDisableDate={shouldDisableCalendarDate}
          onChange={(date) => {
            viewImageContext.changeDate(viewLandContext.land, date);
            setOpenedDatePicker(false);
          }}
          onOpen={() => setOpenedDatePicker(true)}
          onClose={() => setOpenedDatePicker(false)}
          animateYearScrolling
          DialogProps={{
            maxWidth: false,
            PaperProps: {
              className: "view-land-date-picker-dialog",
            },
          }}
          TextFieldComponent={() => null}
        />
      )}
    </>
  );
};

export default DateSelector;
